/**
 * 前端文件解析：统一输出纯文本
 * 支持 txt / md / docx / pdf
 */

/**
 * @param {File} file
 * @returns {Promise<string>} 解析出的纯文本
 */

function getSiteRoot() {
    const href = window.location.href;
    const beforeHash = href.split('#')[0];          // 去掉 hash
    const pathOnly = beforeHash.replace(/[^/]*$/, ''); // 去掉最后一段文件名
    return pathOnly.endsWith('/') ? pathOnly : pathOnly + '/';
  }

export function parseFile(file) {
    const name = (file.name || '').toLowerCase();
  
    if (/\.(txt|md|markdown|csv|json)$/.test(name)) {
      return readAsText(file);
    }
    if (/\.(docx)$/.test(name)) {
      return parseDocx(file);
    }
    if (/\.(doc)$/.test(name)) {
      return Promise.reject(new Error('不支持旧版 .doc 格式，请另存为 .docx 后上传'));
    }
    if (/\.pdf$/.test(name)) {
      return parsePdf(file);
    }
    if (/\.(xlsx|xls)$/.test(name)) {
      return parseExcel(file);
    }
    if (/\.html?$/.test(name)) {          // .html 和 .htm
      return parseHtml(file);
    }
    return Promise.reject(new Error('不支持的文件类型'));
  }
  
  // ---------- 纯文本 ----------
  export function readAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || '').trim());
      reader.onerror = () => reject(new Error('文件读取失败'));
      reader.readAsText(file, 'utf-8');
    });
  }
  
  // ---------- docx（mammoth 提取文本与基本结构） ----------
  export async function parseDocx(file) {
    const mammoth = await import(/* webpackChunkName: "mammoth" */ 'mammoth/mammoth.browser');
    const arrayBuffer = await file.arrayBuffer();
    // extractRawText：只要纯文本；若想保留标题层级可用 convertToHtml 后再剥离标签
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = (result.value || '').trim();
    if (!text) throw new Error('docx 中未提取到文本内容');
    return text;
  }
  
  // ---------- pdf（pdfjs-dist 逐页提取文本） ----------
  export async function parsePdf(file) {
    const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf');
    // 关键：指定 worker 地址，避免 webpack 配置 worker-loader
    pdfjsLib.GlobalWorkerOptions.workerSrc =
        getSiteRoot() + 'pdf.worker.min.js';
  
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  
    const pages = [];
    const maxPages = Math.min(pdf.numPages, 100); // 防止超大文档卡死
    for (let i = 1; i <= maxPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      // 按 y 坐标分组拼接，尽量还原段落
      const text = content.items
        .map(item => item.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (text) pages.push(text);
    }
    const full = pages.join('\n\n').trim();
    if (!full) throw new Error('PDF 中未提取到文本内容（可能是扫描件，需 OCR）');
    return full;
  }

  export async function parseExcel(file) {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ 'xlsx');
    const arrayBuffer = await file.arrayBuffer();
    const wb = XLSX.read(arrayBuffer, { type: 'array' });
  
    const sheets = [];
    for (const sheetName of wb.SheetNames) {
      // 每行单元格用空格拼接，行与行换行，保留表格结构感
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], {
        header: 1,   // 输出二维数组，而不是按首行做 key 的对象
        defval: '',  // 空单元格补空串
        raw: false,  // 日期等格式转为文本
      });
      const text = rows
        .map(row => row.map(cell => String(cell).trim()).filter(Boolean).join(' '))
        .filter(Boolean)
        .join('\n');
      if (text) {
        sheets.push(wb.SheetNames.length > 1 ? `【${sheetName}】\n${text}` : text);
      }
    }
  
    const full = sheets.join('\n\n').trim();
    if (!full) throw new Error('Excel 中未提取到内容');
    return full;
  }

  // ---------- HTML（浏览器原生剥标签，提取正文） ----------
  export async function parseHtml(file) {
    const raw = await readAsText(file);
    const doc = new DOMParser().parseFromString(raw, 'text/html');

    // 去掉对正文无贡献的元素
    doc.querySelectorAll('script, style, noscript, iframe, svg').forEach(el => el.remove());

    // 优先取 <body> 文本，保留块级换行
    const root = doc.body || doc.documentElement;
    const text = (root.innerText || root.textContent || '')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    if (!text) throw new Error('HTML 中未提取到文本内容');
    return text;
  }